import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { GuideWithSteps } from "@shared/modules/guides";
import { useAuthStore } from "../../../core/stores/auth";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Badge } from "../../../core/components/ui/badge";
import { Icon } from "../../../core/components/ui/icon";
import { guidesApi, publicGuideUrl } from "../lib/api";
import { GuideReader } from "../components/guide-reader";
import { GuideStatusBadge, GuideVisibilityBadge } from "../components/badges";

/** Lecteur dans le shell : /guides/:slug. */
export function GuideReaderPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const [guide, setGuide] = useState<GuideWithSteps | null | undefined>(undefined);

  useEffect(() => {
    setGuide(undefined);
    guidesApi
      .bySlug(slug)
      .then(setGuide)
      .catch(() => setGuide(null));
  }, [slug]);

  if (guide === undefined) {
    return (
      <div className="page">
        <p className="muted">Chargement…</p>
      </div>
    );
  }

  if (guide === null) {
    return (
      <div className="page">
        <PageHeader title="Guide introuvable" subtitle="Il n'existe pas, n'est pas publié ou ne vous est pas accessible." />
        <div className="empty">
          <Button variant="ghost" onClick={() => navigate("/guides")}>
            Retour aux guides
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={guide.title}
        subtitle={guide.description ?? undefined}
        actions={
          <>
            <span className="guide-meta">
              <GuideVisibilityBadge visibility={guide.visibility} />
              {isAdmin ? <GuideStatusBadge status={guide.status} /> : null}
              {guide.version > 0 ? <Badge variant="neutral">v{guide.version}</Badge> : null}
            </span>
            {guide.visibility === "public" && guide.status === "published" ? (
              <a className="guide-meta__public" href={publicGuideUrl(guide.slug)} target="_blank" rel="noopener noreferrer">
                Lien public
                <Icon name="external" size={14} />
              </a>
            ) : null}
            {isAdmin ? (
              <Button variant="ghost" icon={<Icon name="edit" size={16} />} onClick={() => navigate(`/guides/${guide.id}/modifier`)}>
                Modifier
              </Button>
            ) : null}
          </>
        }
      />
      <GuideReader guide={guide} />
    </div>
  );
}
